import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
  IconButton,
  Chip,
  Grid,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Palette as PaletteIcon,
  Edit as EditIcon,
  Upload as UploadIcon,
  CheckCircle as CheckCircleIcon,
  ImageSearch as ImageSearchIcon,
  Close as CloseIcon,
  Warning as WarningIcon,
  Badge as BadgeIcon,
  SwapHoriz as SwapHorizIcon,
  Style as StyleIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useStore } from 'react-admin';
import type { ColorPropertyInput, Partner } from '@src/types';
import * as RewareApi from '@src/apis/reware';
import { uploadToS3 } from '@src/apis/reware/catalog/uploadToS3';
import namer from 'color-namer';
import { extractDominantColor } from './colorUtils';

const steps = ['Start', 'Color Identity', 'Color Presentation', 'Color Groups', 'Color Synonyms', 'Review'];

export const AddColorCodeMapping: React.FC = () => {
  const navigate = useNavigate();
  const [currentPartner] = useStore<Partner | null>('currentPartner', null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const colorInputRef = useRef<HTMLInputElement>(null);
  const detectInputRef = useRef<HTMLInputElement>(null);
  const swatchInputRef = useRef<HTMLInputElement>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [entryMethod, setEntryMethod] = useState<'upload' | 'manual'>('manual');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [formData, setFormData] = useState<ColorPropertyInput>({
    code: '',
    display: '',
    hex_code: '',
    url: '',
    synonyms: [],
    family: [],
  });

  const [newSynonym, setNewSynonym] = useState('');
  const [newFamily, setNewFamily] = useState('');
  const [detectImagePreview, setDetectImagePreview] = useState<string>('');
  const [swatchPreview, setSwatchPreview] = useState<string>('');
  const [swatchUploading, setSwatchUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string>('');
  const [isDuplicate, setIsDuplicate] = useState(false);
  const [checkingDuplicate, setCheckingDuplicate] = useState(false);
  const [hexInputMethod, setHexInputMethod] = useState<'enter' | 'select' | 'detect'>('enter');
  const [analyzingImage, setAnalyzingImage] = useState(false);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleBackToList = () => {
    navigate('/catalog/attributes?tab=colors');
  };

  // Check for duplicate color code
  useEffect(() => {
    const checkDuplicate = async () => {
      if (!formData.code || !currentPartner) {
        setIsDuplicate(false);
        return;
      }

      setCheckingDuplicate(true);
      try {
        const response = await RewareApi.getColorProperties({
          partnerCode: currentPartner.code,
          filter: { searchQuery: formData.code },
          page: 1,
          perPage: 100,
        });

        // Check if exact match exists
        const exactMatch = response.data.find(
          prop => prop.code.toLowerCase() === formData.code.toLowerCase()
        );
        setIsDuplicate(!!exactMatch);
      } catch (error) {
        console.error('Failed to check for duplicate:', error);
      } finally {
        setCheckingDuplicate(false);
      }
    };

    // Debounce the check
    const timeoutId = setTimeout(checkDuplicate, 500);
    return () => clearTimeout(timeoutId);
  }, [formData.code, currentPartner]);

  const updateField = (field: keyof ColorPropertyInput, value: string | string[]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const suggestCodeAndDisplayFromColor = (hexColor: string) => {
    const match = namer(hexColor).ntc[0];
    if (!match) return { code: '', display: '' };

    const cleaned = match.name
      .replace(/[^\w\s\-]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    // Suggest code: uppercase, replace spaces with underscores, limit to 20 chars
    const code = cleaned
      .toUpperCase()
      .replace(/\s+/g, '_')
      .replace(/[^A-Z0-9_]/g, '')
      .substring(0, 20);

    // Suggest display: title case, limit to 50 chars
    const display = cleaned
      .split(/\s+/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
      .substring(0, 50);

    return { code, display };
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadedFile(file);
    setEntryMethod('upload');
    setAnalyzingImage(true);

    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;

      const img = new Image();
      img.onload = () => {
        // Detect the dominant color and suggest a code/display name from it
        const hexColor = extractDominantColor(img);
        const { code, display } = suggestCodeAndDisplayFromColor(hexColor);

        updateField('hex_code', hexColor);
        if (code || display) {
          updateField('code', code);
          updateField('display', display);
        }

        setAnalyzingImage(false);
        handleNext();
      };
      img.src = result;
    };
    reader.readAsDataURL(file);
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleSave = async () => {
    if (!currentPartner) {
      setSaveError('No partner selected. Please refresh and try again.');
      return;
    }

    if (isDuplicate) {
      setSaveError(`Color code "${formData.code}" already exists. Please use a different code or edit the existing mapping.`);
      return;
    }

    setSaving(true);
    setSaveError('');

    try {
      const token = localStorage.getItem('token') || '';

      await RewareApi.createColorProperty({
        partnerCode: currentPartner.code,
        token,
        data: formData,
      });

      // Navigate back to list on success
      navigate('/catalog/attributes?tab=colors');
    } catch (error) {
      console.error('Failed to save color property:', error);

      // Extract error message from various error formats
      let errorMessage = 'Failed to save color mapping. Please try again.';

      if (error?.response?.data?.errors?.[0]?.message) {
        errorMessage = error.response.data.errors[0].message;
      } else if (error?.message) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }

      // Check for common error types
      if (errorMessage.toLowerCase().includes('duplicate') || errorMessage.toLowerCase().includes('already exists')) {
        errorMessage = `Color code "${formData.code}" already exists. Please use a different code.`;
      }

      setSaveError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const addSynonym = () => {
    if (newSynonym.trim()) {
      updateField('synonyms', [...(formData.synonyms || []), newSynonym.trim()]);
      setNewSynonym('');
    }
  };

  const removeSynonym = (index: number) => {
    const newSynonyms = [...(formData.synonyms || [])];
    newSynonyms.splice(index, 1);
    updateField('synonyms', newSynonyms);
  };

  const addFamily = () => {
    if (newFamily.trim()) {
      updateField('family', [...(formData.family || []), newFamily.trim()]);
      setNewFamily('');
    }
  };

  const removeFamily = (index: number) => {
    const newFamilies = [...(formData.family || [])];
    newFamilies.splice(index, 1);
    updateField('family', newFamilies);
  };

  const handleDetectImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadError('');

    if (!file.type.startsWith('image/')) {
      setUploadError('Please upload an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setUploadError('Image must be smaller than 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      setDetectImagePreview(result);

      // Create image element to extract color
      const img = new Image();
      img.onload = () => {
        const hexColor = extractDominantColor(img);
        updateField('hex_code', hexColor);
      };
      img.src = result;
    };
    reader.readAsDataURL(file);
  };

  const clearDetectImage = () => {
    setDetectImagePreview('');
    if (detectInputRef.current) {
      detectInputRef.current.value = '';
    }
  };

  const handleSwatchUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadError('');

    if (!file.type.startsWith('image/')) {
      setUploadError('Please upload an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setUploadError('Image must be smaller than 5MB');
      return;
    }

    // Show preview immediately
    const reader = new FileReader();
    reader.onload = (e) => {
      setSwatchPreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);

    if (!currentPartner) {
      setUploadError('No partner selected');
      return;
    }

    const ext = file.name.split('.').pop() || 'png';
    const contentType = file.type;

    setSwatchUploading(true);
    try {
      const { uploadUrl, publicUrl } = await RewareApi.getColorSwatchUploadUrl({
        partnerCode: currentPartner.code,
        colorCode: formData.code || 'new',
        fileExtension: ext,
        contentType,
      });

      await uploadToS3({ presignedUrl: uploadUrl, file, contentType });

      updateField('url', publicUrl);
    } catch (error) {
      console.error('Swatch upload failed:', error);
      setUploadError('Failed to upload swatch image. Please try again.');
      setSwatchPreview('');
    } finally {
      setSwatchUploading(false);
    }
  };

  const clearSwatchImage = () => {
    updateField('url', '');
    setSwatchPreview('');
    if (swatchInputRef.current) {
      swatchInputRef.current.value = '';
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0: // Start
        return (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PaletteIcon /> How to Add Color
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
              Choose your preferred method for adding this color code mapping.
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card
                  variant="outlined"
                  sx={{
                    height: '100%',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: 'primary.main',
                      boxShadow: 2,
                    },
                  }}
                  onClick={handleUploadClick}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ textAlign: 'center', mb: 2 }}>
                      <UploadIcon sx={{ fontSize: 48, color: 'primary.main' }} />
                    </Box>
                    <Typography variant="h6" gutterBottom textAlign="center">
                      Upload a product or swatch image
                    </Typography>
                    <Typography variant="body2" color="text.secondary" textAlign="center">
                      We&apos;ll extract the image and generate the hex code, display name, and color groups. You can review and edit everything before saving.
                    </Typography>
                    <Box sx={{ textAlign: 'center', mt: 3 }}>
                      <Button variant="contained" startIcon={<UploadIcon />}>
                        Choose this method
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card
                  variant="outlined"
                  sx={{
                    height: '100%',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: 'primary.main',
                      boxShadow: 2,
                    },
                  }}
                  onClick={() => {
                    setEntryMethod('manual');
                    handleNext();
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ textAlign: 'center', mb: 2 }}>
                      <EditIcon sx={{ fontSize: 48, color: 'primary.main' }} />
                    </Box>
                    <Typography variant="h6" gutterBottom textAlign="center">
                      Enter details manually
                    </Typography>
                    <Typography variant="body2" color="text.secondary" textAlign="center">
                      Enter the color mapping details yourself: the color code, display name, hex value, swatch, groups, and synonyms.
                    </Typography>
                    <Box sx={{ textAlign: 'center', mt: 3 }}>
                      <Button variant="contained" startIcon={<EditIcon />}>
                        Choose this method
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        );

      case 1: // Color Identity
        return (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BadgeIcon /> Color Identity
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
              The catalog code and the customer-facing name it maps to.
            </Typography>

            {entryMethod === 'upload' && uploadedFile && (
              <Card variant="outlined" sx={{ mb: 3, p: 2, backgroundColor: '#f5f9ff' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="subtitle2">
                    Uploaded Image
                  </Typography>
                  {analyzingImage && <CircularProgress size={16} />}
                </Box>
                <Typography variant="body2" color="text.secondary">
                  <strong>{uploadedFile.name}</strong> ({(uploadedFile.size / 1024).toFixed(2)} KB)
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  {analyzingImage ?
                    'Analyzing image to detect the dominant color and suggest a name...' :
                    'We detected the dominant color in this image and suggested a name. You can review and edit the generated details below.'}
                </Typography>
              </Card>
            )}

            <TextField
              label="Color Code"
              required
              fullWidth
              value={formData.code}
              onChange={(e) => updateField('code', e.target.value)}
              error={isDuplicate}
              helperText={
                isDuplicate ?
                  `Code "${formData.code}" already exists` :
                  checkingDuplicate ?
                    'Checking for duplicates...' :
                    'The code used in your catalog data (e.g., "NVY", "001")'
              }
              sx={{ mb: 3 }}
              InputProps={{
                endAdornment: checkingDuplicate ? (
                  <CircularProgress size={20} />
                ) : isDuplicate ? (
                  <WarningIcon color="error" />
                ) : null,
              }}
            />

            {isDuplicate && (
              <Alert severity="error" sx={{ mb: 3 }}>
                This color code already exists. Please choose a different code, or go back to the list and edit the existing mapping.
              </Alert>
            )}

            <TextField
              label="Color Display Name"
              required
              fullWidth
              value={formData.display}
              onChange={(e) => updateField('display', e.target.value)}
              helperText='Customer-facing color name (e.g., "Midnight Blue")'
            />
          </Box>
        );

      case 2: // Color Presentation
        return (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PaletteIcon /> Color Presentation
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
              Set the hex value and optional swatch image shown on the storefront.
            </Typography>

            {uploadError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setUploadError('')}>
                {uploadError}
              </Alert>
            )}

            {/* Current Hex Code Display */}
            <Card variant="outlined" sx={{ p: 3, mb: 3, backgroundColor: '#f9f9f9' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    backgroundColor: formData.hex_code || '#000000',
                    border: '2px solid #e0e0e0',
                    borderRadius: 2,
                    flexShrink: 0,
                  }}
                />
                <Box>
                  <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600 }}>
                    CURRENT HEX CODE
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    {formData.hex_code || '#000000'}
                  </Typography>
                </Box>
              </Box>
            </Card>

            {/* Hex Code Input Methods */}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Choose how you&apos;d like to set the hex code:
            </Typography>

            <Box sx={{ display: 'flex', gap: 0, mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Button
                variant={hexInputMethod === 'enter' ? 'contained' : 'outlined'}
                onClick={() => setHexInputMethod('enter')}
                sx={{
                  flex: 1,
                  borderRadius: 0,
                  borderTopLeftRadius: 4,
                  borderBottomLeftRadius: 4,
                  border: 'none',
                  '&.MuiButton-outlined': {
                    border: 'none',
                    backgroundColor: 'transparent',
                  },
                }}
                startIcon={<EditIcon />}
              >
                Enter a hex code
              </Button>
              <Button
                variant={hexInputMethod === 'select' ? 'contained' : 'outlined'}
                onClick={() => setHexInputMethod('select')}
                sx={{
                  flex: 1,
                  borderRadius: 0,
                  border: 'none',
                  borderLeft: '1px solid',
                  borderRight: '1px solid',
                  borderColor: 'divider',
                  '&.MuiButton-outlined': {
                    border: 'none',
                    borderLeft: '1px solid',
                    borderRight: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: 'transparent',
                  },
                }}
                startIcon={<PaletteIcon />}
              >
                Select a color
              </Button>
              <Button
                variant={hexInputMethod === 'detect' ? 'contained' : 'outlined'}
                onClick={() => setHexInputMethod('detect')}
                sx={{
                  flex: 1,
                  borderRadius: 0,
                  borderTopRightRadius: 4,
                  borderBottomRightRadius: 4,
                  border: 'none',
                  '&.MuiButton-outlined': {
                    border: 'none',
                    backgroundColor: 'transparent',
                  },
                }}
                startIcon={<ImageSearchIcon />}
              >
                Detect from image
              </Button>
            </Box>

            {/* Enter Hex Code Method */}
            {hexInputMethod === 'enter' && (
              <Card variant="outlined" sx={{ p: 3, mb: 3 }}>
                <TextField
                  label="Hex Code"
                  fullWidth
                  value={formData.hex_code}
                  onChange={(e) => updateField('hex_code', e.target.value)}
                  placeholder="#000000"
                  helperText="Enter a hex code like #FF5733"
                />
              </Card>
            )}

            {/* Select Color Method */}
            {hexInputMethod === 'select' && (
              <Card variant="outlined" sx={{ p: 3, mb: 3 }}>
                <input
                  ref={colorInputRef}
                  type="color"
                  value={formData.hex_code || '#000000'}
                  onChange={(e) => updateField('hex_code', e.target.value)}
                  style={{ display: 'none' }}
                />
                <Button
                  variant="outlined"
                  startIcon={<PaletteIcon />}
                  onClick={() => colorInputRef.current?.click()}
                  fullWidth
                >
                  Open Color Picker
                </Button>
              </Card>
            )}

            {/* Detect from Image Method */}
            {hexInputMethod === 'detect' && (
              <Card variant="outlined" sx={{ p: 3, mb: 3 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Upload an image and we&apos;ll automatically detect the dominant color
                </Typography>
                <input
                  ref={detectInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleDetectImageUpload}
                  style={{ display: 'none' }}
                />
                {!detectImagePreview ? (
                  <Button
                    variant="outlined"
                    startIcon={<ImageSearchIcon />}
                    onClick={() => detectInputRef.current?.click()}
                    fullWidth
                  >
                    Upload Image to Detect Color
                  </Button>
                ) : (
                  <Box sx={{ position: 'relative', display: 'inline-block' }}>
                    <img
                      src={detectImagePreview}
                      alt="Color detection"
                      style={{
                        maxWidth: '200px',
                        maxHeight: '200px',
                        borderRadius: '4px',
                        border: '1px solid #e0e0e0',
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={clearDetectImage}
                      sx={{
                        position: 'absolute',
                        top: -8,
                        right: -8,
                        backgroundColor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'divider',
                        '&:hover': { backgroundColor: 'background.paper' },
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </Card>
            )}

            {/* Color Swatch Image */}
            <Card variant="outlined" sx={{ p: 3 }}>
              <Typography variant="subtitle1" gutterBottom fontWeight="medium" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <StyleIcon /> Color Swatch Image
              </Typography>
              <input
                ref={swatchInputRef}
                type="file"
                accept="image/png,image/jpeg"
                onChange={handleSwatchUpload}
                style={{ display: 'none' }}
              />
              {formData.url || swatchPreview ? (
                <Box>
                  <Box
                    sx={{
                      width: 300,
                      p: 2,
                      border: '2px dashed',
                      borderColor: 'divider',
                      borderRadius: 2,
                      backgroundColor: '#f5f5f5',
                      textAlign: 'center',
                      position: 'relative',
                    }}
                  >
                    <img
                      src={formData.url || swatchPreview}
                      alt="Color swatch"
                      style={{
                        maxWidth: '100px',
                        maxHeight: '100px',
                        borderRadius: '8px',
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={clearSwatchImage}
                      sx={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        backgroundColor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'divider',
                        '&:hover': { backgroundColor: 'background.paper' },
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                      SWATCH
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Box
                  onClick={() => swatchInputRef.current?.click()}
                  sx={{
                    width: 300,
                    p: 4,
                    border: '2px dashed',
                    borderColor: 'divider',
                    borderRadius: 2,
                    backgroundColor: '#f5f5f5',
                    textAlign: 'center',
                    cursor: swatchUploading ? 'default' : 'pointer',
                    transition: 'border-color 0.2s',
                    '&:hover': swatchUploading ? {} : { borderColor: 'primary.main' },
                  }}
                >
                  {swatchUploading ? (
                    <CircularProgress size={32} />
                  ) : (
                    <>
                      <ImageSearchIcon sx={{ fontSize: 32, color: 'text.secondary', mb: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        Click to upload a swatch
                      </Typography>
                    </>
                  )}
                  <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1, fontWeight: 'bold' }}>
                    SWATCH
                  </Typography>
                </Box>
              )}
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Optional. Used in place of the hex swatch on the storefront. PNG or JPG, up to 5 MB.
              </Typography>
            </Card>
          </Box>
        );

      case 3: // Color Groups
        return (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PaletteIcon /> Color Groups
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
              Groups drive storefront color filtering. Add as many as apply.
            </Typography>

            <Card variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Color Groups
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Used for storefront filtering. A color can belong to multiple groups (e.g., Blue & Grey).
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                {formData.family?.map((group, index) => (
                  <Chip
                    key={index}
                    label={group}
                    onDelete={() => removeFamily(index)}
                  />
                ))}
              </Box>
              <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                <TextField
                  size="small"
                  placeholder="Add color group"
                  value={newFamily}
                  onChange={(e) => setNewFamily(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addFamily()}
                  fullWidth
                />
                <Button onClick={addFamily} variant="outlined">
                  Add
                </Button>
              </Box>
            </Card>
          </Box>
        );

      case 4: // Color Synonyms
        return (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SwapHorizIcon /> Color Synonyms
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
              Other color codes (e.g., from garment tags or supplier feeds) that should map to this color.
            </Typography>

            <Card variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Color Synonyms
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Any catalog record using one of these codes will resolve to this color mapping.
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                {formData.synonyms?.map((synonym, index) => (
                  <Chip
                    key={index}
                    label={synonym}
                    onDelete={() => removeSynonym(index)}
                  />
                ))}
              </Box>
              <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                <TextField
                  size="small"
                  placeholder="Add synonym"
                  value={newSynonym}
                  onChange={(e) => setNewSynonym(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addSynonym()}
                  fullWidth
                />
                <Button onClick={addSynonym} variant="outlined">
                  Add
                </Button>
              </Box>
            </Card>
          </Box>
        );

      case 5: // Review
        return (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PaletteIcon /> Review
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
              Confirm the mapping before saving.
            </Typography>

            {saveError && (
              <Alert severity="error" sx={{ mb: 3 }} onClose={() => setSaveError('')}>
                {saveError}
              </Alert>
            )}

            {isDuplicate && (
              <Alert severity="error" sx={{ mb: 3 }}>
                Color code &quot;
                {formData.code}
                &quot; already exists. Please go back and choose a different code.
              </Alert>
            )}

            <Card variant="outlined">
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Color Code
                    </Typography>
                    <Typography variant="body1">{formData.code || '—'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Color Display Name
                    </Typography>
                    <Typography variant="body1">{formData.display || '—'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Color Hex Code
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {formData.hex_code && (
                        <Box
                          sx={{
                            width: 20,
                            height: 20,
                            backgroundColor: formData.hex_code,
                            border: '1px solid #ccc',
                            borderRadius: 1,
                          }}
                        />
                      )}
                      <Typography variant="body1">{formData.hex_code || '—'}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Swatch Image
                    </Typography>
                    {formData.url ? (
                      <img
                        src={formData.url}
                        alt="Color swatch"
                        style={{
                          maxWidth: '60px',
                          maxHeight: '60px',
                          borderRadius: '4px',
                          border: '1px solid #e0e0e0',
                          marginTop: '4px',
                        }}
                      />
                    ) : (
                      <Typography variant="body1">—</Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Color Groups
                    </Typography>
                    <Typography variant="body1">{formData.family?.join(', ') || '—'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Color Synonyms
                    </Typography>
                    <Typography variant="body1">{formData.synonyms?.join(', ') || '—'}</Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Box>
        );

      default:
        return null;
    }
  };

  const isStepComplete = () => {
    switch (activeStep) {
      case 0: // Start
        return true; // Always can proceed
      case 1: // Color Identity
        return formData.code && formData.display && !isDuplicate && !checkingDuplicate;
      case 2: // Color Presentation
        return true; // All optional
      case 3: // Color Groups
        return true; // Optional
      case 4: // Color Synonyms
        return true; // Optional
      case 5: // Review
        return !isDuplicate && !saving; // Can't save if duplicate or already saving
      default:
        return false;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PaletteIcon /> Add Color Code Mapping
        </Typography>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={handleBackToList}
        >
          Back to Code Mappings
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        Map a catalog code to its display name, grouping, and storefront presentation
      </Typography>

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label, index) => (
          <Step key={label} completed={index < activeStep}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Card>
        <CardContent sx={{ p: 4 }}>
          {renderStepContent()}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button onClick={activeStep === 0 ? handleBackToList : handleBack}>
              {activeStep === 0 ? '← Back' : 'Back'}
            </Button>
            {activeStep === 0 ? (
              // No Next button on step 0 - the choice cards handle navigation
              <div />
            ) : activeStep === steps.length - 1 ? (
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={!isStepComplete()}
                startIcon={saving ? <CircularProgress size={20} /> : <CheckCircleIcon />}
              >
                {saving ? 'Saving...' : 'Save Color Code Mapping'}
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={!isStepComplete()}
              >
                Next →
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Hidden file input for upload method */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />
    </Box>
  );
};
